// Plain copy for the /tools hub — no icons, no "use client", so it is safe to
// import from server code (the /api/md/tools route) as well as the client section.

export interface ExtensionCopy {
  slug: "chrome" | "firefox" | "mcp";
  label: string;
  href: string;
  description: string;
}

export interface ToolCopy {
  slug: string;
  label: string;
  description: string;
}

export interface AvailableWebToolCopy extends ToolCopy {
  href: string;
}

export interface WhyToolCopy {
  slug: string;
  title: string;
  description: string;
}

export interface ToolsFaq {
  q: string;
  a: string;
}

export const EXTENSIONS_COPY: ExtensionCopy[] = [
  {
    slug: "chrome",
    label: "Proxidize Chrome Extension",
    href: "/tools/chrome-extension",
    description:
      "Manage and switch between multiple HTTP(S) and SOCKS proxies in Chrome with a single click — free and easy to use.",
  },
  {
    slug: "firefox",
    label: "Proxidize Firefox Extension",
    href: "/tools/firefox-extension",
    description:
      "Manage and switch between multiple proxies in Firefox right from your toolbar — free and easy to use.",
  },
  {
    slug: "mcp",
    label: "Proxidize MCP Server",
    href: "/tools/mcp-server",
    description:
      "Rotate IPs, switch locations, and monitor usage from Claude, Cursor, and any MCP client — free and open source.",
  },
];

export const AVAILABLE_WEB_TOOLS_COPY: AvailableWebToolCopy[] = [
  {
    slug: "fingerprint",
    label: "Browser Fingerprint",
    href: "/tools/fingerprint",
    description:
      "Inspect the repeatable browser signals sites can combine into a cookie-free snapshot.",
  },
  {
    slug: "webrtc-leak",
    label: "WebRTC Leak Test",
    href: "/tools/webrtc-leak",
    description:
      "See whether WebRTC exposes your real IP address while you sit behind a proxy or VPN.",
  },
  {
    slug: "user-agent",
    label: "User Agent Checker",
    href: "/tools/user-agent",
    description:
      "Parse a user-agent string — browser, engine, OS, device and the signals your browser exposes.",
  },
  {
    slug: "ip-checker",
    label: "IP Checker",
    href: "/tools/ip-checker",
    description:
      "See your public IP and what it reveals — city, region, country, ISP/organisation and ASN.",
  },
];

export interface UseCaseCopy {
  slug: string;
  text: string;
  /** Optional internal links when a live page covers this use case. */
  links?: { label: string; href: string }[];
}

export interface ProductPairCopy {
  slug: "mobile" | "residential";
  label: string;
  href: string;
  description: string;
}

export const COMING_SOON_COPY: ToolCopy[] = [
  {
    slug: "proxy-checker",
    label: "Proxy Checker",
    description:
      "Test HTTP, HTTPS, SOCKS4 & SOCKS5 proxies — status, latency, speed, exit IP, location and anonymity.",
  },
  {
    slug: "ip-lookup",
    label: "IP Lookup",
    description:
      "Look up any IP address or domain — geolocation, ISP, ASN, timezone and reverse DNS.",
  },
  {
    slug: "my-location",
    label: "My Location",
    description:
      "Compare your IP-based location with your real GPS position on a map to spot a mismatch.",
  },
  {
    slug: "proxy-bandwidth-checker",
    label: "Proxy Bandwidth Checker",
    description:
      "Measure a proxy's real download speed and latency by streaming a file through it — HTTP, HTTPS, SOCKS4 & SOCKS5.",
  },
];

export const WHY_TOOLS_COPY: WhyToolCopy[] = [
  {
    slug: "free",
    title: "Free, no sign-up",
    description:
      "Everything here is completely free — no account, no paywall and no usage limits. The extensions install in one click from the official stores.",
  },
  {
    slug: "private",
    title: "Private by design",
    description:
      "The Browser Fingerprint report is computed locally and isn't sent to or stored on our servers.",
  },
  {
    slug: "browser",
    title: "Runs in your browser",
    description:
      "No downloads or config for the web tools — open a tab and go, on desktop or mobile. The extensions live right in your toolbar.",
  },
  {
    slug: "accurate",
    title: "Accurate where it counts",
    description:
      "Repeatable, varied, blocked and unavailable reads stay distinct so the result never overclaims what the browser revealed.",
  },
  {
    slug: "realtime",
    title: "Results in real time",
    description:
      "The fingerprint scan runs when you open the page, with slower browser APIs completing as they become available.",
  },
  {
    slug: "export",
    title: "Copy & export",
    description:
      "Copy the snapshot hash or download the detailed JSON report for your own comparison and review.",
  },
];

export const USE_CASES: UseCaseCopy[] = [
  {
    slug: "switch",
    text: "Switch your browser between proxies in one click while you work.",
    links: [
      { label: "Chrome extension", href: "/tools/chrome-extension" },
      { label: "Firefox extension", href: "/tools/firefox-extension" },
    ],
  },
  {
    slug: "confirm",
    text: "Confirm a VPN or proxy actually changes the IP and location sites see.",
  },
  {
    slug: "vet",
    text: "Vet a freshly bought or scraped proxy list before relying on it.",
  },
  {
    slug: "webrtc",
    text: "Spot whether WebRTC is leaking your real IP from behind a proxy.",
    links: [{ label: "WebRTC Leak Test", href: "/tools/webrtc-leak" }],
  },
  {
    slug: "fingerprint",
    text: "See the browser signals sites can combine into a cookie-free fingerprint.",
    links: [{ label: "Browser Fingerprint", href: "/tools/fingerprint" }],
  },
  {
    slug: "investigate",
    text: "Investigate an unfamiliar IP or domain: who owns it and where it sits.",
  },
];

export const PRIVACY_POINTS: string[] = [
  "The fingerprint hash and detailed signal report stay in your browser",
  "Fingerprint results aren't sent to or stored on Proxidize servers",
  "No account or sign-up required",
  "The page uses the same consent-based site analytics as the rest of proxidize.com",
  "Served over encrypted HTTPS",
];

export const PRODUCT_PAIRINGS: ProductPairCopy[] = [
  {
    slug: "mobile",
    label: "Mobile Proxies",
    href: "/proxy-server/mobile-proxies",
    description:
      "Ready-to-use 5G/4G rotating mobile proxies on a managed US network — sticky or rotating sessions with city-level targeting.",
  },
  {
    slug: "residential",
    label: "Residential Proxies",
    href: "/proxy-server/residential-proxies",
    description:
      "Ethically sourced rotating residential IPs across 195+ countries, with rollover bandwidth that never expires.",
  },
];

export const TOOLS_FAQS: ToolsFaq[] = [
  {
    q: "Are these tools free to use?",
    a: "Yes — every tool here is free, with no account, no paywall and no usage limits. The browser extensions are free to install from the Chrome Web Store and Firefox Add-ons.",
  },
  {
    q: "Do the extensions work with proxies from any provider?",
    a: "Yes. The Proxidize extensions work with any HTTP(S) or SOCKS proxy, from any provider — and pair with Proxidize proxies for the best results.",
  },
  {
    q: "Do you store my data?",
    a: "The Browser Fingerprint hash and detailed report are computed locally and aren't sent to or stored on our servers. The page uses the same consent-based site analytics as the rest of proxidize.com.",
  },
  {
    q: "Do I need to create an account?",
    a: "No. None of the tools require an account or login. Just open a tool — or install an extension — and go.",
  },
  {
    q: "When will the coming-soon tools launch?",
    a: "We're rolling them out one by one. Browser Fingerprint, the WebRTC Leak Test, the User Agent Checker, the IP Checker and the Chrome and Firefox extensions are available today; Proxy Checker, IP Lookup, My Location and Proxy Bandwidth Checker are still on the way.",
  },
  {
    q: "What's the difference between the Chrome and Firefox extensions?",
    a: "Both give you one-click proxy switching, automatic detection of proxy type, backconnect IP and country, testing before you connect, and manual, bulk or CSV import. Each is built natively for its browser, so the features match closely.",
  },
  {
    q: "What is the Proxidize MCP server?",
    a: "It's a free, open-source MCP (Model Context Protocol) server that lets AI agents like Claude and Cursor manage your Proxidize proxies — rotating IPs, switching locations, and checking usage — directly through natural language.",
  },
];
